import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../App';

const MAINT_ICONS = ['🔧','🔥','💧','⚡','🌡️','🪟','🚿','🛁','🧹','🏠','🌿','🔑','📡','🧯'];
const VEHICLE_MAINT_TYPES = [
  { type: 'Vidange', icon: '🛢️', months: 12 },
  { type: 'Révision complète', icon: '🔧', months: 24 },
  { type: 'Contrôle technique', icon: '📋', months: 24 },
  { type: 'Pneumatiques', icon: '🔄', months: 36 },
  { type: 'Freins', icon: '🛑', months: 24 },
  { type: 'Distribution', icon: '⚙️', months: 60 },
  { type: 'Batterie', icon: '🔋', months: 48 },
  { type: 'Climatisation', icon: '❄️', months: 24 },
];
const STICKY_COLORS = ['#FFE082','#A5D6A7','#90CAF9','#F48FB1','#CE93D8','#FFCC80'];
const STOCK_CATS = ['Frigo','Congélateur','Placards','Produits ménagers','Salle de bain','Autre'];

export default function Maison() {
  const { user } = useAuth();
  const [tab, setTab] = useState('stickies');

  // Pense-bête
  const [stickies, setStickies] = useState([]);
  const [stickyInput, setStickyInput] = useState('');
  const [stickyColor, setStickyColor] = useState('#FFE082');

  // Stock
  const [stock, setStock] = useState([]);
  const [showStockForm, setShowStockForm] = useState(false);
  const [editStock, setEditStock] = useState(null);
  const [stockForm, setStockForm] = useState({ name: '', category: 'Frigo', quantity: 1, min_quantity: 1, unit: '', auto_add_to_cart: true });

  // Entretien maison
  const [homeMaint, setHomeMaint] = useState([]);
  const [showHomeMaintForm, setShowHomeMaintForm] = useState(false);
  const [editHomeMaint, setEditHomeMaint] = useState(null);
  const [homeMaintForm, setHomeMaintForm] = useState({ name: '', icon: '🔧', last_date: '', interval_months: 12, notes: '' });

  // Véhicules
  const [vehicles, setVehicles] = useState([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ name: '', brand: '', model: '', plate: '', year: '', color: '#6C63FF' });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [maintForm, setMaintForm] = useState({ type: 'Vidange', icon: '🛢️', done_date: '', done_km: '', next_date: '', next_km: '', notes: '' });

  // Week-end
  const [activities, setActivities] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [newActivity, setNewActivity] = useState({ name: '', emoji: '🎯', weather: 'any', with_child: true });

  const [saving, setSaving] = useState(false);
  const _d = new Date(); const today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;

  const load = useCallback(() => {
    api.get('/stickies').then(r => setStickies(r.data)).catch(() => {});
    api.get('/stock').then(r => setStock(r.data)).catch(() => {});
    api.get('/home-maintenance').then(r => setHomeMaint(r.data)).catch(() => {});
    api.get('/vehicles').then(r => setVehicles(r.data)).catch(() => {});
    api.get('/weekend-activities').then(r => setActivities(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadWeather = async () => {
    setWeatherLoading(true);
    try {
      // Open-Meteo gratuit — coordonnées Le Gua / Marennes (17600)
      const r = await fetch('https://api.open-meteo.com/v1/forecast?latitude=45.74&longitude=-0.99&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=Europe%2FParis&forecast_days=7');
      const data = await r.json();
      // Trouver le prochain samedi et dimanche
      const today = new Date();
      const days = data.daily.time.map((d, i) => ({
        date: d, day: new Date(d).getDay(),
        code: data.daily.weathercode[i],
        max: Math.round(data.daily.temperature_2m_max[i]),
        min: Math.round(data.daily.temperature_2m_min[i]),
        rain: data.daily.precipitation_sum[i]
      }));
      const weekend = days.filter(d => d.day === 6 || d.day === 0);
      setWeather(weekend.slice(0, 2));
    } catch (e) { setWeather([]); }
    setWeatherLoading(false);
  };

  useEffect(() => { if (tab === 'weekend') loadWeather(); }, [tab]);

  const getWeatherInfo = (code) => {
    if (code <= 3) return { icon: '☀️', label: 'Ensoleillé', type: 'sunny' };
    if (code <= 48) return { icon: '⛅', label: 'Nuageux', type: 'sunny' };
    if (code <= 67) return { icon: '🌧️', label: 'Pluvieux', type: 'rainy' };
    if (code <= 77) return { icon: '❄️', label: 'Neige', type: 'rainy' };
    return { icon: '⛈️', label: 'Orageux', type: 'rainy' };
  };

  // Pense-bête
  const addSticky = async () => {
    if (!stickyInput.trim()) return;
    await api.post('/stickies', { content: stickyInput, color: stickyColor });
    setStickyInput('');
    load();
  };
  const toggleSticky = async (id, done) => { await api.put(`/stickies/${id}`, { done: !done }); load(); };
  const deleteSticky = async (id) => { await api.delete(`/stickies/${id}`); load(); };

  // Stock
  const saveStock = async () => {
    setSaving(true);
    try {
      if (editStock) await api.put(`/stock/${editStock.id}`, stockForm);
      else await api.post('/stock', stockForm);
      setShowStockForm(false); setEditStock(null);
      load();
    } catch(e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const addToCart = async (id) => { await api.post(`/stock/${id}/add-to-cart`); alert('✅ Ajouté à la liste de courses !'); };
  const openStockEdit = (item) => {
    setStockForm({ name: item.name, category: item.category, quantity: item.quantity, min_quantity: item.min_quantity, unit: item.unit, auto_add_to_cart: !!item.auto_add_to_cart });
    setEditStock(item); setShowStockForm(true);
  };

  // Entretien maison
  const saveHomeMaint = async () => {
    setSaving(true);
    try {
      if (editHomeMaint) await api.put(`/home-maintenance/${editHomeMaint.id}`, homeMaintForm);
      else await api.post('/home-maintenance', homeMaintForm);
      setShowHomeMaintForm(false); setEditHomeMaint(null);
      load();
    } catch(e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const openHomeMaintEdit = (item) => {
    setHomeMaintForm({ name: item.name, icon: item.icon, last_date: item.last_date?.split('T')[0]||'', interval_months: item.interval_months, notes: item.notes||'' });
    setEditHomeMaint(item); setShowHomeMaintForm(true);
  };

  // Véhicules
  const saveVehicle = async () => {
    setSaving(true);
    try {
      if (editVehicle) await api.put(`/vehicles/${editVehicle.id}`, vehicleForm);
      else await api.post('/vehicles', vehicleForm);
      setShowVehicleForm(false); setEditVehicle(null);
      load();
    } catch(e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };
  const saveMaint = async () => {
    setSaving(true);
    try {
      await api.post(`/vehicles/${selectedVehicle.id}/maintenance`, maintForm);
      setShowMaintForm(false);
      load();
    } catch(e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const overdueHomeMaint = homeMaint.filter(m => m.next_due && m.next_due < today);
  const upcomingHomeMaint = homeMaint.filter(m => m.next_due && m.next_due >= today);
  const lowStock = stock.filter(s => s.quantity <= s.min_quantity);

  return (
    <div>
      <div className="topbar">
        <div className="page-title">🏡 Maison</div>
        {tab === 'stock' && lowStock.length > 0 && (
          <span style={{ background: '#EF5350', color: 'white', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{lowStock.length} en rupture</span>
        )}
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        {/* TABS */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
          {[['stickies','💬 Notes'],['stock','📦 Stock'],['entretien','🔧 Entretien'],['vehicules','🚗 Voitures'],['weekend','☀️ Week-end']].map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)}
              style={{ padding: '7px 14px', borderRadius: 20, border: `2px solid ${tab===val?'#6C63FF':'#E0E4F0'}`, background: tab===val?'#EEF0FF':'white', fontSize: 12, fontWeight: tab===val?700:500, color: tab===val?'#6C63FF':'#888', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {label}
            </button>
          ))}
        </div>

        {/* ===== PENSE-BÊTE ===== */}
        {tab === 'stickies' && (
          <>
            <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <textarea value={stickyInput} onChange={e => setStickyInput(e.target.value)}
                placeholder="Écrire une note partagée... (Ex: Rappeler dentiste, Acheter cadeau école, Appeler plombier)"
                rows={2} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), addSticky())}
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 14, resize: 'none', fontFamily: 'inherit', background: 'transparent' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {STICKY_COLORS.map(c => (
                  <button key={c} onClick={() => setStickyColor(c)}
                    style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: stickyColor===c?'3px solid #333':'2px solid transparent', cursor: 'pointer' }} />
                ))}
                <button onClick={addSticky} disabled={!stickyInput.trim()}
                  style={{ marginLeft: 'auto', background: '#6C63FF', color: 'white', border: 'none', borderRadius: 10, padding: '6px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  + Envoyer
                </button>
              </div>
            </div>

            {stickies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <div className="empty-text">Aucune note<br />Écrivez un pense-bête pour toute la famille</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {stickies.map(s => (
                  <div key={s.id} style={{ background: s.done ? '#F5F5F5' : s.color, borderRadius: 14, padding: '14px 14px 10px', position: 'relative', opacity: s.done ? 0.6 : 1, minHeight: 90 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333', textDecoration: s.done?'line-through':'none', lineHeight: 1.5, marginBottom: 8 }}>{s.content}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: '#888' }}>{s.author_avatar} {s.author_name}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => toggleSticky(s.id, s.done)}
                          style={{ background: s.done?'#E0E0E0':'#33333322', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12 }}>
                          {s.done ? '↩️' : '✓'}
                        </button>
                        <button onClick={() => deleteSticky(s.id)}
                          style={{ background: '#EF535022', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12, color: '#EF5350' }}>×</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== STOCK ===== */}
        {tab === 'stock' && (
          <>
            {lowStock.length > 0 && (
              <div style={{ background: '#FFEBEE', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, color: '#C62828', marginBottom: 6, fontSize: 13 }}>⚠️ À racheter</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {lowStock.map(s => (
                    <div key={s.id} style={{ background: 'white', borderRadius: 8, padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{s.name}</span>
                      <button onClick={() => addToCart(s.id)} style={{ background: '#EF5350', color: 'white', border: 'none', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>+ courses</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 14 }} onClick={() => { setEditStock(null); setStockForm({ name: '', category: 'Frigo', quantity: 1, min_quantity: 1, unit: '', auto_add_to_cart: true }); setShowStockForm(true); }}>
              + Ajouter un produit
            </button>

            {STOCK_CATS.map(cat => {
              const items = stock.filter(s => s.category === cat);
              if (!items.length) return null;
              return (
                <div key={cat} style={{ marginBottom: 12 }}>
                  <div className="section-title">{cat}</div>
                  {items.map(item => (
                    <div key={item.id} style={{ background: item.quantity <= item.min_quantity ? '#FFF3E0' : 'white', borderRadius: 12, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: item.quantity <= item.min_quantity ? '#E65100' : '#AAAACC' }}>
                          Stock : {item.quantity} {item.unit} · Min : {item.min_quantity} {item.unit}
                          {item.quantity <= item.min_quantity && ' ⚠️ À racheter'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => { api.put(`/stock/${item.id}`, {...item, quantity: Math.max(0, item.quantity-1)}); load(); }}
                          style={{ width: 28, height: 28, borderRadius: 8, border: '2px solid #E0E4F0', background: 'white', cursor: 'pointer', fontWeight: 700 }}>−</button>
                        <span style={{ width: 24, textAlign: 'center', lineHeight: '28px', fontWeight: 700, fontSize: 14 }}>{item.quantity}</span>
                        <button onClick={() => { api.put(`/stock/${item.id}`, {...item, quantity: item.quantity+1}); load(); }}
                          style={{ width: 28, height: 28, borderRadius: 8, border: '2px solid #E0E4F0', background: 'white', cursor: 'pointer', fontWeight: 700 }}>+</button>
                        <button onClick={() => openStockEdit(item)} style={{ width: 28, height: 28, borderRadius: 8, border: '2px solid #E0E4F0', background: 'white', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {stock.length === 0 && <div className="empty-state"><div className="empty-icon">📦</div><div className="empty-text">Stock vide<br />Ajoutez vos produits essentiels</div></div>}
          </>
        )}

        {/* ===== ENTRETIEN MAISON ===== */}
        {tab === 'entretien' && (
          <>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 14 }} onClick={() => { setEditHomeMaint(null); setHomeMaintForm({ name: '', icon: '🔧', last_date: today, interval_months: 12, notes: '' }); setShowHomeMaintForm(true); }}>
              + Ajouter un entretien
            </button>

            {overdueHomeMaint.length > 0 && (
              <>
                <div className="section-title" style={{ color: '#EF5350' }}>⚠️ En retard ({overdueHomeMaint.length})</div>
                {overdueHomeMaint.map(item => <MaintCard key={item.id} item={item} today={today} onEdit={() => { openHomeMaintEdit(item); }} />)}
              </>
            )}
            {upcomingHomeMaint.length > 0 && (
              <>
                <div className="section-title">📅 À venir</div>
                {upcomingHomeMaint.map(item => <MaintCard key={item.id} item={item} today={today} onEdit={() => { openHomeMaintEdit(item); }} />)}
              </>
            )}
            {homeMaint.length === 0 && <div className="empty-state"><div className="empty-icon">🔧</div><div className="empty-text">Aucun entretien enregistré<br />Chaudière, chauffe-eau, VMC...</div></div>}
          </>
        )}

        {/* ===== VÉHICULES ===== */}
        {tab === 'vehicules' && (
          <>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 14 }} onClick={() => { setEditVehicle(null); setVehicleForm({ name: '', brand: '', model: '', plate: '', year: '', color: '#6C63FF' }); setShowVehicleForm(true); }}>
              + Ajouter un véhicule
            </button>

            {vehicles.map(v => (
              <div key={v.id} style={{ background: 'white', borderRadius: 16, padding: 16, marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: v.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🚗</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{v.name}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{v.brand} {v.model} {v.year && `· ${v.year}`} {v.plate && `· ${v.plate}`}</div>
                  </div>
                  <button onClick={() => { setEditVehicle(v); setVehicleForm({ name: v.name, brand: v.brand, model: v.model, plate: v.plate, year: v.year||'', color: v.color }); setShowVehicleForm(true); }}
                    style={{ background: '#F0F2FF', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                </div>

                {v.maintenance?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {v.maintenance.slice(0, 3).map(m => (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F0F2FF', fontSize: 13 }}>
                        <span>{m.icon}</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>{m.type}</span>
                        {m.done_date && <span style={{ color: '#888', fontSize: 11 }}>Fait : {new Date(m.done_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                        {m.next_date && <span style={{ color: m.next_date < today ? '#EF5350' : '#4CAF50', fontSize: 11, fontWeight: 700 }}>
                          {m.next_date < today ? '⚠️' : '→'} {new Date(m.next_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>}
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={() => { setSelectedVehicle(v); setMaintForm({ type: 'Vidange', icon: '🛢️', done_date: today, done_km: '', next_date: '', next_km: '', notes: '' }); setShowMaintForm(true); }}
                  className="btn btn-ghost btn-sm" style={{ width: '100%', fontSize: 12 }}>+ Ajouter un entretien</button>
              </div>
            ))}
            {vehicles.length === 0 && <div className="empty-state"><div className="empty-icon">🚗</div><div className="empty-text">Aucun véhicule<br />Ajoutez votre voiture pour suivre les entretiens</div></div>}
          </>
        )}

        {/* ===== WEEK-END ===== */}
        {tab === 'weekend' && (
          <>
            {weatherLoading && <div style={{ textAlign: 'center', padding: 20 }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>}

            {weather && weather.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {weather.map(w => {
                  const info = getWeatherInfo(w.code);
                  return (
                    <div key={w.date} style={{ background: 'white', borderRadius: 14, padding: 14, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#8888AA', textTransform: 'uppercase', marginBottom: 4 }}>
                        {new Date(w.date + 'T12:00').toLocaleDateString('fr-FR', { weekday: 'long' })}
                      </div>
                      <div style={{ fontSize: 36, margin: '6px 0' }}>{info.icon}</div>
                      <div style={{ fontWeight: 700, color: '#333' }}>{w.max}° / {w.min}°</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{info.label}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {weather && (
              <>
                <div className="section-title">
                  {weather[0] ? `💡 Idées pour ce week-end (${getWeatherInfo(weather[0]?.code).type === 'sunny' ? 'beau temps ☀️' : 'temps couvert 🌧️'})` : '💡 Idées activités'}
                </div>
                {(() => {
                  const wtype = weather[0] ? getWeatherInfo(weather[0].code).type : 'any';
                  const suggestions = activities.filter(a => a.weather === 'any' || a.weather === wtype);
                  const shuffled = [...suggestions].sort(() => Math.random() - 0.5).slice(0, 6);
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                      {shuffled.map(a => (
                        <div key={a.id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 22 }}>{a.emoji}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#333355' }}>{a.name}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <button className="btn btn-ghost btn-sm" style={{ width: '100%', marginBottom: 16 }} onClick={loadWeather}>🔄 Autres suggestions</button>
              </>
            )}

            <div className="section-title">➕ Ajouter une idée</div>
            <div className="card">
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="form-input" placeholder="Ex: Skate park..." value={newActivity.name} onChange={e => setNewActivity({ ...newActivity, name: e.target.value })} style={{ flex: 1 }} />
                <input className="form-input" value={newActivity.emoji} onChange={e => setNewActivity({ ...newActivity, emoji: e.target.value })} style={{ width: 56, textAlign: 'center' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <select className="form-input" value={newActivity.weather} onChange={e => setNewActivity({ ...newActivity, weather: e.target.value })}>
                  <option value="any">Tous temps</option>
                  <option value="sunny">Beau temps ☀️</option>
                  <option value="rainy">Temps couvert 🌧️</option>
                </select>
                <select className="form-input" value={newActivity.with_child} onChange={e => setNewActivity({ ...newActivity, with_child: e.target.value === 'true' })}>
                  <option value="true">Avec enfant 👧</option>
                  <option value="false">Adultes seulement</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={async () => { if (!newActivity.name) return; await api.post('/weekend-activities', newActivity); setNewActivity({ name: '', emoji: '🎯', weather: 'any', with_child: true }); load(); }}>
                + Ajouter
              </button>
            </div>
          </>
        )}
      </div>

      {/* MODAL STOCK */}
      {showStockForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowStockForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editStock ? 'Modifier' : '📦 Nouveau produit'}</div>
            <div className="form-group"><label className="form-label">Nom</label>
              <input className="form-input" value={stockForm.name} onChange={e => setStockForm({...stockForm, name: e.target.value})} placeholder="Ex: Lait, Farine..." autoFocus /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Catégorie</label>
                <select className="form-input" value={stockForm.category} onChange={e => setStockForm({...stockForm, category: e.target.value})}>
                  {STOCK_CATS.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div className="form-group"><label className="form-label">Unité</label>
                <input className="form-input" value={stockForm.unit} onChange={e => setStockForm({...stockForm, unit: e.target.value})} placeholder="L, kg, pcs..." /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Stock actuel</label>
                <input className="form-input" type="number" value={stockForm.quantity} onChange={e => setStockForm({...stockForm, quantity: Number(e.target.value)})} /></div>
              <div className="form-group"><label className="form-label">Seuil alerte</label>
                <input className="form-input" type="number" value={stockForm.min_quantity} onChange={e => setStockForm({...stockForm, min_quantity: Number(e.target.value)})} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {editStock && <button className="btn btn-danger btn-sm" onClick={async () => { await api.delete(`/stock/${editStock.id}`); setShowStockForm(false); load(); }}>🗑️</button>}
              <button className="btn btn-ghost" onClick={() => setShowStockForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveStock} disabled={saving || !stockForm.name} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ENTRETIEN MAISON */}
      {showHomeMaintForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowHomeMaintForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editHomeMaint ? 'Modifier' : '🔧 Nouvel entretien'}</div>
            <div className="form-group"><label className="form-label">Icône</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {MAINT_ICONS.map(ic => (
                  <button key={ic} type="button" onClick={() => setHomeMaintForm({...homeMaintForm, icon: ic})}
                    style={{ width: 36, height: 36, border: homeMaintForm.icon === ic ? '2px solid #6C63FF' : '2px solid #E0E4F0', borderRadius: 8, background: homeMaintForm.icon === ic ? '#EEF0FF' : '#F5F6FF', fontSize: 18, cursor: 'pointer' }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group"><label className="form-label">Nom</label>
              <input className="form-input" value={homeMaintForm.name} onChange={e => setHomeMaintForm({...homeMaintForm, name: e.target.value})} placeholder="Ex: Chaudière, VMC, Chauffe-eau..." /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Dernier entretien</label>
                <input className="form-input" type="date" value={homeMaintForm.last_date} onChange={e => setHomeMaintForm({...homeMaintForm, last_date: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Tous les (mois)</label>
                <input className="form-input" type="number" value={homeMaintForm.interval_months} onChange={e => setHomeMaintForm({...homeMaintForm, interval_months: Number(e.target.value)})} /></div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label>
              <input className="form-input" value={homeMaintForm.notes} onChange={e => setHomeMaintForm({...homeMaintForm, notes: e.target.value})} placeholder="Technicien, contact, références..." /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              {editHomeMaint && <button className="btn btn-danger btn-sm" onClick={async () => { await api.delete(`/home-maintenance/${editHomeMaint.id}`); setShowHomeMaintForm(false); load(); }}>🗑️</button>}
              <button className="btn btn-ghost" onClick={() => setShowHomeMaintForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveHomeMaint} disabled={saving || !homeMaintForm.name} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VÉHICULE */}
      {showVehicleForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowVehicleForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editVehicle ? 'Modifier le véhicule' : '🚗 Nouveau véhicule'}</div>
            <div className="form-group"><label className="form-label">Surnom</label>
              <input className="form-input" value={vehicleForm.name} onChange={e => setVehicleForm({...vehicleForm, name: e.target.value})} placeholder="Ex: Clio, 308, Kangoo..." autoFocus /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Marque</label>
                <input className="form-input" value={vehicleForm.brand} onChange={e => setVehicleForm({...vehicleForm, brand: e.target.value})} placeholder="Renault..." /></div>
              <div className="form-group"><label className="form-label">Modèle</label>
                <input className="form-input" value={vehicleForm.model} onChange={e => setVehicleForm({...vehicleForm, model: e.target.value})} placeholder="Clio..." /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Immatriculation</label>
                <input className="form-input" value={vehicleForm.plate} onChange={e => setVehicleForm({...vehicleForm, plate: e.target.value})} placeholder="DY-405-CA" /></div>
              <div className="form-group"><label className="form-label">Année</label>
                <input className="form-input" type="number" value={vehicleForm.year} onChange={e => setVehicleForm({...vehicleForm, year: e.target.value})} placeholder="2018" /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {editVehicle && <button className="btn btn-danger btn-sm" onClick={async () => { if (!window.confirm('Supprimer ce véhicule ?')) return; await api.delete(`/vehicles/${editVehicle.id}`); setShowVehicleForm(false); load(); }}>🗑️</button>}
              <button className="btn btn-ghost" onClick={() => setShowVehicleForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveVehicle} disabled={saving || !vehicleForm.name} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ENTRETIEN VÉHICULE */}
      {showMaintForm && selectedVehicle && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowMaintForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">🔧 Entretien — {selectedVehicle.name}</div>
            <div className="form-group"><label className="form-label">Type d'entretien</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {VEHICLE_MAINT_TYPES.map(t => (
                  <button key={t.type} type="button" onClick={() => {
                    const nd = new Date(maintForm.done_date || today);
                    nd.setMonth(nd.getMonth() + t.months);
                    setMaintForm({...maintForm, type: t.type, icon: t.icon, next_date: `${nd.getFullYear()}-${String(nd.getMonth()+1).padStart(2,'0')}-${String(nd.getDate()).padStart(2,'0')}`});
                  }}
                    style={{ padding: '6px 10px', borderRadius: 10, border: maintForm.type===t.type?'2px solid #6C63FF':'2px solid #E0E4F0', background: maintForm.type===t.type?'#EEF0FF':'white', fontSize: 12, cursor: 'pointer', fontWeight: maintForm.type===t.type?700:400 }}>
                    {t.icon} {t.type}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Date effectuée</label>
                <input className="form-input" type="date" value={maintForm.done_date} onChange={e => setMaintForm({...maintForm, done_date: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Km effectué</label>
                <input className="form-input" type="number" value={maintForm.done_km} onChange={e => setMaintForm({...maintForm, done_km: e.target.value})} placeholder="Ex: 85000" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group"><label className="form-label">Prochain entretien</label>
                <input className="form-input" type="date" value={maintForm.next_date} onChange={e => setMaintForm({...maintForm, next_date: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Km prochain</label>
                <input className="form-input" type="number" value={maintForm.next_km} onChange={e => setMaintForm({...maintForm, next_km: e.target.value})} placeholder="Ex: 95000" /></div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label>
              <input className="form-input" value={maintForm.notes} onChange={e => setMaintForm({...maintForm, notes: e.target.value})} placeholder="Garage, prix, remarques..." /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setShowMaintForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveMaint} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MaintCard({ item, today, onEdit }) {
  const isOverdue = item.next_due && item.next_due < today;
  const months = item.next_due ? Math.round((new Date(item.next_due) - new Date()) / (1000*60*60*24*30)) : null;
  return (
    <div onClick={onEdit} style={{ background: isOverdue ? '#FFF3F3' : 'white', borderLeft: `4px solid ${isOverdue ? '#EF5350' : '#4CAF50'}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22 }}>{item.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{item.name}</div>
          <div style={{ fontSize: 11, color: isOverdue ? '#EF5350' : '#888', marginTop: 2 }}>
            {isOverdue ? '⚠️ En retard — ' : 'Prochain : '}
            {item.next_due ? new Date(item.next_due + 'T12:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            {!isOverdue && months !== null && months <= 3 && ` (dans ${months} mois)`}
          </div>
          {item.notes && <div style={{ fontSize: 11, color: '#AAAACC', marginTop: 2 }}>{item.notes}</div>}
        </div>
        <span style={{ color: '#BBBBCC', fontSize: 18 }}>›</span>
      </div>
    </div>
  );
}
